import { lazy } from "react"
import { Navigate, type RouteObject } from "react-router-dom"
import { RequireAuth } from "@/features/auth/RequireAuth"
import { RequireAdminAccess } from "@/features/admin/RequireAdminAccess"
import MainLayout from "@/widgets/layout/MainLayout"
import {
  PORTAL_DEPOSIT,
  PORTAL_EVENTS,
  PORTAL_EVENTS_CREATED,
  PORTAL_EVENTS_MODERATION,
  PORTAL_EVENTS_REGISTRATIONS,
} from "@/app/router/portalRoutes"
import { RequireDepositModerationAccess } from "@/features/deposits/RequireDepositModerationAccess"
import { LegacyEventDetailRedirect } from "@/app/router/LegacyEventDetailRedirect"

const HomePage = lazy(() => import("@/pages/home/page"))
const EventsPage = lazy(() => import("@/pages/events/page"))
const EventDetailPage = lazy(() => import("@/pages/events/EventDetailPage"))
const DepositsPage = lazy(() => import("@/pages/deposits/page"))
const DepositDetailPage = lazy(() => import("@/pages/deposits/DepositDetailPage"))
const EventsPortalPage = lazy(() => import("@/pages/events/portal/page"))
const EventsPortalLayout = lazy(() => import("@/pages/events/portal/EventsPortalLayout"))
const EventPreviewPage = lazy(() => import("@/pages/events/portal/EventPreviewPage"))
const EventParticipantProfilePage = lazy(() => import("@/pages/events/portal/EventParticipantProfilePage"))
const EventAttendancePage = lazy(() => import("@/pages/events/portal/EventAttendancePage"))
const DepositPortalLayout = lazy(() => import("@/pages/deposit/portal/DepositPortalLayout"))
const DepositsMyPage = lazy(() => import("@/pages/deposit/portal/DepositsMyPage"))
const DepositMyDetailPage = lazy(() => import("@/pages/deposit/portal/DepositMyDetailPage"))
const DepositIntroPage = lazy(() => import("@/pages/deposit/portal/DepositIntroPage"))
const DepositsModerationPage = lazy(() => import("@/pages/deposit/portal/DepositsModerationPage"))
const DepositModerationDetailPage = lazy(() => import("@/pages/deposit/portal/DepositModerationDetailPage"))
const EventsPortalIndexRedirect = lazy(() => import("@/pages/events/portal/EventsPortalIndexRedirect"))
const DepositPortalRegistryPage = lazy(() => import("@/pages/deposit/portal/DepositPortalRegistryPage"))
const ContactsPage = lazy(() => import("@/pages/contacts/page"))
const AboutPage = lazy(() => import("@/pages/about/page"))
const PrototypePage = lazy(() => import("@/pages/prototype/page"))
const ProfilePage = lazy(() => import("@/pages/profile/page"))
const AdminPage = lazy(() => import("@/pages/admin/page"))
const AuthPage = lazy(() => import("@/pages/auth/page"))
const ResetPasswordPage = lazy(() => import("@/pages/reset-password/page"))
const ConfirmEmailPage = lazy(() => import("@/pages/confirm-email/page"))
const AgreementPage = lazy(() => import("@/pages/legal/AgreementPage"))
const PersonalDataConsentPage = lazy(() => import("@/pages/legal/PersonalDataConsentPage"))
const PolicyPrivacyPage = lazy(() => import("@/pages/legal/PolicyPrivacyPage"))
const NotFound = lazy(() => import("@/pages/NotFound"))

const routes: RouteObject[] = [
  { path: "/login", element: <AuthPage /> },
  { path: "/reset-password", element: <ResetPasswordPage /> },
  { path: "/confirm-email", element: <ConfirmEmailPage /> },
  {
    path: "/",
    element: <MainLayout />,
    children: [
      { index: true, element: <HomePage /> },
      { path: "events", element: <EventsPage /> },
      { path: "events/portal", element: <Navigate to={PORTAL_EVENTS} replace /> },
      { path: "events/:id", element: <LegacyEventDetailRedirect /> },
      { path: "deposits", element: <DepositsPage /> },
      { path: "deposits/:id", element: <DepositDetailPage /> },
      {
        path: "portal",
        children: [
          { index: true, element: <Navigate to="events" replace /> },
          {
            path: "deposit",
            element: (
              <RequireAuth>
                <DepositPortalLayout />
              </RequireAuth>
            ),
            children: [
              { index: true, element: <Navigate to="my" replace /> },
              { path: "registry", element: <DepositPortalRegistryPage /> },
              { path: "registry/:id", element: <DepositDetailPage embeddedInPortal /> },
              { path: "my", element: <DepositsMyPage /> },
              { path: "my/:depositId", element: <DepositMyDetailPage /> },
              { path: "apply", element: <DepositIntroPage /> },
              { path: "apply/:depositId", element: <Navigate to="../my" replace /> },
              {
                path: "moderation",
                element: (
                  <RequireDepositModerationAccess>
                    <DepositsModerationPage />
                  </RequireDepositModerationAccess>
                ),
              },
              {
                path: "moderation/:depositId",
                element: (
                  <RequireDepositModerationAccess>
                    <DepositModerationDetailPage />
                  </RequireDepositModerationAccess>
                ),
              },
            ],
          },
          {
            path: "events",
            element: <EventsPortalLayout />,
            children: [
              {
                index: true,
                element: (
                  <RequireAuth>
                    <EventsPortalIndexRedirect />
                  </RequireAuth>
                ),
              },
              {
                path: "all",
                element: (
                  <RequireAuth>
                    <EventsPortalPage defaultTab="all" />
                  </RequireAuth>
                ),
              },
              {
                path: "created",
                element: (
                  <RequireAuth>
                    <EventsPortalPage defaultTab="created" />
                  </RequireAuth>
                ),
              },
              {
                path: "moderation",
                element: (
                  <RequireAuth>
                    <EventsPortalPage defaultTab="moderation" />
                  </RequireAuth>
                ),
              },
              {
                path: "registrations",
                element: (
                  <RequireAuth>
                    <EventsPortalPage defaultTab="registrations" />
                  </RequireAuth>
                ),
              },
              {
                path: "attendance",
                element: (
                  <RequireAuth>
                    <EventAttendancePage />
                  </RequireAuth>
                ),
              },
              {
                path: "preview/:eventId/participant/:userId",
                element: (
                  <RequireAuth>
                    <EventParticipantProfilePage />
                  </RequireAuth>
                ),
              },
              {
                path: "preview/:id",
                element: (
                  <RequireAuth>
                    <EventPreviewPage />
                  </RequireAuth>
                ),
              },
              { path: ":id", element: <EventDetailPage /> },
            ],
          },
          { path: "projects/*", element: <Navigate to={PORTAL_EVENTS} replace /> },
          {
            path: "admin",
            element: (
              <RequireAuth>
                <RequireAdminAccess>
                  <AdminPage />
                </RequireAdminAccess>
              </RequireAuth>
            ),
          },
        ],
      },
      { path: "mycreatedevents", element: <Navigate to={PORTAL_EVENTS_CREATED} replace /> },
      { path: "eventsmoderation", element: <Navigate to={PORTAL_EVENTS_MODERATION} replace /> },
      {
        path: "eventsregistrationmoderation",
        element: <Navigate to={PORTAL_EVENTS_REGISTRATIONS} replace />,
      },
      { path: "projects/portal", element: <Navigate to={PORTAL_EVENTS} replace /> },
      { path: "deposit", element: <Navigate to={PORTAL_DEPOSIT} replace /> },
      { path: "contacts", element: <ContactsPage /> },
      { path: "about", element: <AboutPage /> },
      { path: "prototype", element: <PrototypePage /> },
      { path: "help/agreement", element: <AgreementPage /> },
      { path: "help/personal", element: <PersonalDataConsentPage /> },
      { path: "help/privacy", element: <PolicyPrivacyPage /> },
      {
        path: "profile",
        element: (
          <RequireAuth>
            <ProfilePage />
          </RequireAuth>
        ),
      },
      { path: "*", element: <NotFound /> },
    ],
  },
]

export default routes
